import { CaseStudy } from 'types/generated/contentful'
import CaseStudyPage from './_CaseStudyPageTemplate'
import { GetStaticPaths, GetStaticProps } from 'next'
import { contentful } from 'utils/contentful'

type CaseStudyPageProps = {
  caseStudy: CaseStudy
  readMoreCases: CaseStudy[]
}

export default function CaseStudyPreviewPage(
  props: CaseStudyPageProps,
): JSX.Element {
  return <CaseStudyPage {...props} />
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const json = await contentful({
    query: /* GraphQL */ `
      fragment AssetFragment on Asset {
        url
        width
        height
        title
        __typename
        sys {
          id
          __typename
        }
      }

      query getCaseStudy($slug: String!) {
        caseStudyCollection(where: { slug: $slug }, limit: 1) {
          __typename
          items {
            __typename
            sys {
              id
              __typename
            }
            title
            slug
            metaImage {
              ...AssetFragment
            }
            metaDescription
            industry {
              slug
              type
              title
              __typename
            }
            technology {
              slug
              type
              title
              __typename
            }
            quote {
              __typename
              text
              author {
                __typename
                name
                role
                company {
                  __typename
                  name
                  logo {
                    ...AssetFragment
                  }
                  techStackCollection(limit: 50) {
                    __typename
                    items {
                      __typename
                      name
                      logo {
                        ...AssetFragment
                      }
                    }
                  }
                  foundingDate
                  teamSize
                }
              }
            }
            improvementType
            increasedPerformance
            costReduction
            serverLoadReduction
            body {
              __typename
              json
              links {
                __typename
                entries {
                  __typename
                  block {
                    __typename
                    sys {
                      __typename
                      id
                    }
                    ... on CodeBlock {
                      __typename
                      code
                      language
                    }
                    ... on HighlightedLink {
                      __typename
                      type
                      title
                      subline
                      buttonText
                      buttonUrl
                    }
                    ... on AlternatingLayout {
                      __typename
                      label
                      title
                      blocksCollection {
                        __typename
                        items {
                          __typename
                          ... on ComponentVideo {
                            __typename
                            url
                          }
                          ... on AlternatingLayoutItem {
                            __typename
                            sys {
                              __typename
                              id
                            }
                            title
                            text {
                              __typename
                              json
                              links {
                                __typename
                                entries {
                                  block {
                                    __typename
                                    sys {
                                      __typename
                                      id
                                    }
                                  }
                                }
                                assets {
                                  __typename
                                  hyperlink {
                                    ...AssetFragment
                                  }
                                  block {
                                    ...AssetFragment
                                  }
                                }
                              }
                            }
                            image {
                              ...AssetFragment
                            }
                          }
                        }
                      }
                    }
                    ... on TextWithImages {
                      __typename
                      text {
                        __typename
                        json
                        links {
                          __typename
                          entries {
                            __typename
                            block {
                              __typename
                              sys {
                                __typename
                                id
                              }
                              ... on CodeBlock {
                                __typename
                                code
                                language
                              }
                            }
                          }
                          assets {
                            __typename
                            hyperlink {
                              ...AssetFragment
                            }
                            block {
                              ...AssetFragment
                            }
                          }
                        }
                      }
                      imagesCollection {
                        __typename
                        items {
                          ...AssetFragment
                        }
                      }
                    }
                  }
                }
                assets {
                  __typename
                  hyperlink {
                    ...AssetFragment
                  }
                  block {
                    ...AssetFragment
                  }
                }
              }
            }
          }
        }
        readMoreCases: caseStudyCollection(
          where: { slug_not: $slug }
          limit: 2
          order: publishedAt_DESC
        ) {
          __typename
          items {
            sys {
              __typename
              id
            }
            slug
            quote {
              __typename
              text
              author {
                __typename
                name
                role
                company {
                  __typename
                  name
                  logo {
                    ...AssetFragment
                  }
                }
              }
            }
            improvementType
            increasedPerformance
            serverLoadReduction
            costReduction
          }
        }
      }
    `,
    variables: {
      slug: params?.slug,
    },
  })

  if (json.errors && Array.isArray(json.errors) && json.errors.length > 0) {
    throw new Error(
      `Failed to fetch case study page: ${json.errors[0].message}`,
    )
  }

  return {
    props: {
      caseStudy: json?.data.caseStudyCollection.items[0] || null,
      readMoreCases: json?.data.readMoreCases.items || null,
    },
    revalidate: 10, // In seconds
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const json = await contentful({
    query: `query getCaseStudyPaths {
      caseStudyCollection {
        items {
          slug
        }
      }
    }`,
  })

  const paths = json.data.caseStudyCollection.items.map((caseStudy) => ({
    params: { slug: caseStudy.slug },
  }))

  return { paths, fallback: 'blocking' }
}
